import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useMemo, useRef } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { getEditableDayColIds, parseDayColId } from "src/utils/date/dateUtils";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import {
	createCellCssClassRules,
	GridCellConfig,
	useCellChangeHandler,
} from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import {
	defaultColDef,
	getDefaultRowIndexColumnConfig,
} from "src/utils/grid/gridStyleUtils";
import {
	defaultTQC2100_R01DetailPivotDto,
	TQC2100_R01DetailPivotDto,
} from "./Dto_TQC2100_R01";
import { useTQC2100_R01Store } from "./Store_TQC2100_R01";
import {
	buildMonthDayColumnDefs,
	getMonthDayColumns_TQC2100_R01,
	headerNames_TQC2100_R01Detail,
	normalizeMeasureValue,
} from "./Util_TQC2100_R01";

const COLUMN_STATE_KEY = "TQC2100_R01-detail-grid-column-state-v2";
interface Props {
	gridRef: React.RefObject<AgGridReact<TQC2100_R01DetailPivotDto> | null>;
}

/** 점검내용별 일자 측정값을 입력하는 월간 피벗 그리드 */
export const Table_TQC2100_R01 = ({ gridRef }: Props) => {
	const {
		TQC2100_R01Filter,
		TQC2100_R01DetailPivotList,
		originalTQC2100_R01DetailPivotList,
		selectedTQC2100_R01DetailPivotList,
		deletedTQC2100_R01DetailPivotList,
		detailColumnState,
		detailScrollTop,
		setTQC2100_R01DetailPivotList,
		updateTQC2100_R01DetailPivot,
		setDetailColumnState,
		setDetailScrollTop,
	} = useTQC2100_R01Store();
	
	const wrapperRef = useRef<HTMLDivElement>(null);

	const { getRowId, settingRowIndex, paddedGridData, createCellClassRules } = useGridSettingContent({
		gridRef,
		key: "valueIndex",
		gridData: TQC2100_R01DetailPivotList,
		defaultGridData: defaultTQC2100_R01DetailPivotDto,
		selectedRow: undefined,
		originalGridData: originalTQC2100_R01DetailPivotList,
		updateRow: updateTQC2100_R01DetailPivot,
	});

	const monthColumns = useMemo(
		() => getMonthDayColumns_TQC2100_R01(TQC2100_R01Filter.inspectDt)
	, [TQC2100_R01Filter.inspectDt]);

	// 일자 열만 편집과 키보드 이동이 가능하도록 공통 그리드 설정에 전달한다.
	const editableColumns = useMemo(
		() => getEditableDayColIds(monthColumns)
	, [monthColumns]);

	const cssConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: [],
		insertedDisabledCells: [],
		commonDisabledCells: [],
		pointerCells: editableColumns,
		insertedPointerCells: [],
		commonPointerCells: [],
		alignLeftCells: ["inspItemNm"],
	}), [editableColumns]);

	const cssRules = useMemo(() => createCellCssClassRules(cssConfig), [cssConfig]);
	const cellCssDefaultColDef = useMemo(() => ({
		...defaultColDef,
		cellClassRules: {
			...createCellClassRules<TQC2100_R01DetailPivotDto>({
				idKey: "valueIndex",
				selectedList: selectedTQC2100_R01DetailPivotList,
				deletedList: deletedTQC2100_R01DetailPivotList,
				...cssRules,
			}),
		},
	}), [createCellClassRules, cssRules, selectedTQC2100_R01DetailPivotList, deletedTQC2100_R01DetailPivotList]);
	const handleCellChange = useCellChangeHandler({
		dataList: TQC2100_R01DetailPivotList,
		originalDataList: originalTQC2100_R01DetailPivotList,
		updateRow: updateTQC2100_R01DetailPivot,
		customBeforeUpdate: (updatedData, field, newValue) => {
			const dayDd = parseDayColId(field);
			if (!dayDd) {
				return updatedData;
			}
			const normalizedValue = normalizeMeasureValue(newValue);
			const originalRow = originalTQC2100_R01DetailPivotList.find(
				(row) => row.valueIndex === updatedData.valueIndex,
			);
			const originalDayValue = originalRow?.dayValues?.[dayDd] ?? "";
			const isModified = updatedData.inserted
				? !!updatedData.isModified
				: String(originalDayValue) !== String(normalizedValue);
			const { [field]: _removed, ...rest } = updatedData as TQC2100_R01DetailPivotDto &
				Record<string, unknown>;
			return {
				...rest,
				dayValues: {
					...updatedData.dayValues,
					[dayDd]: normalizedValue,
				},
				isModified,
			} as TQC2100_R01DetailPivotDto;
		},
	});
	const { handleCellClick, handleCellKeyDown } = useDetailTableEvents({
		detailList: TQC2100_R01DetailPivotList,
		editableColumns,
		keyDownConfig: { enableArrowNavigation: true },
	});
	const baseColumnDefs: ColDef<TQC2100_R01DetailPivotDto>[] = useMemo(() => {
		const dayColumnDefs = buildMonthDayColumnDefs(TQC2100_R01Filter.inspectDt);
		return [
			...getDefaultRowIndexColumnConfig(),
			{
				headerName: "점검내용", colId: "inspItemNm", field: "inspItemNm", width: 160,
				minWidth: 120,
				editable: false,
			},
			...dayColumnDefs,
		];
	}, [TQC2100_R01Filter.inspectDt]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs = useMemo(() => {
		if (detailColumnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<TQC2100_R01DetailPivotDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames: headerNames_TQC2100_R01Detail,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [baseColumnDefs, detailColumnState, settingRowIndex]);

	const pinnedBottomRow = useMemo(
		() => [{ inspItemNm: `${TQC2100_R01DetailPivotList.length.toLocaleString()}건` }]
	, [TQC2100_R01DetailPivotList]);

	return (
		<div className="w-full h-full flex flex-col gap-[4px]">
			<GridContextWrapper
				gridWrapperRef={wrapperRef}
				gridRef={gridRef}
				paddedGridData={paddedGridData}
				setPaddedGridData={setTQC2100_R01DetailPivotList}
				defaultRowData={defaultTQC2100_R01DetailPivotDto}
				baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
				defaultColumnState={defaultColumnState}
				setColumnState={setDetailColumnState}
				localStorageKey={COLUMN_STATE_KEY}
				menuItems={undefined}
			>
				<Grid_Excel
					ref={gridRef}
					rowData={paddedGridData}
					getRowId={getRowId}
					columnDefs={columnDefs}
					defaultColDef={cellCssDefaultColDef}
					columnStateKey={COLUMN_STATE_KEY}
					setColumnState={setDetailColumnState}
					getDefaultColumnState={() => defaultColumnState}
					scrollTop={detailScrollTop}
					setScrollTop={setDetailScrollTop}
					onCellValueChanged={handleCellChange}
					onCellClicked={handleCellClick}
					onCellKeyDown={handleCellKeyDown}
					pinnedBottomRowData={pinnedBottomRow}
				/>
			</GridContextWrapper>
		</div>
	);
};
